import React, { useState } from 'react';
import { getGeminiApiKey, saveGeminiApiKey, getGeminiModel, saveGeminiModel } from '../services/geminiService';
import { Key, ShieldCheck, Save, Cpu } from 'lucide-react';

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApiKeyModal: React.FC<ApiKeyModalProps> = ({ isOpen, onClose }) => {
  const [apiKey, setApiKey] = useState<string>(getGeminiApiKey());
  const [selectedModel, setSelectedModel] = useState<string>(getGeminiModel());
  const [saved, setSaved] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleSave = () => {
    saveGeminiApiKey(apiKey);
    saveGeminiModel(selectedModel);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 flex items-center justify-center animate-fade-in">
      <div className="relative max-w-md w-full bg-dark-card border border-slate-700/80 p-6 rounded-2xl shadow-2xl space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
          <div className="flex items-center gap-2 text-brand-glow font-bold text-lg">
            <Key className="w-5 h-5 text-brand-400" /> Gemini AI Configuration
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg">✕</button>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed">
          Configure your Gemini AI Studio API key and preferred active model.
        </p>

        {/* API Key */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-300">Gemini API Key</label>
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Paste your Gemini API key here..."
            className="w-full bg-dark-bg border border-slate-700 rounded-xl px-3.5 py-2 text-sm text-white font-mono focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <div className="text-[11px] text-slate-400">Stored locally in your browser's localStorage.</div>
        </div>

        {/* Model Selector */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5 text-cyan-400" /> Select Active Gemini Model
          </label>
          <select
            value={selectedModel}
            onChange={e => setSelectedModel(e.target.value)}
            className="w-full bg-dark-bg border border-slate-700 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-brand-500 font-sans text-xs"
          >
            <option value="gemini-flash-latest">gemini-flash-latest (Recommended - Ultra Fast)</option>
            <option value="gemini-3.6-flash">gemini-3.6-flash (Next-Gen Multimodal)</option>
            <option value="gemini-pro-latest">gemini-pro-latest (High Precision Reasoning)</option>
          </select>
        </div>

        <div className="pt-2 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-xs font-semibold shadow-glow-blue transition-all"
          >
            {saved ? <ShieldCheck className="w-4 h-4 text-emerald-300" /> : <Save className="w-4 h-4" />}
            {saved ? 'Saved!' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};
